import { User } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";
import { approvalService, ApprovalStatus } from "../services/ApprovalService";
import { signOutUser } from "../services/firebase";
import { createLogger } from "../utils/logger";

const logger = createLogger("useApprovalStatus");

export interface UseApprovalStatusReturn {
  approvalStatus: ApprovalStatus | null;
  isLoading: boolean;
  error: string | null;
  isApproved: boolean;
  isPending: boolean;
  isRejected: boolean;
  refetch: () => Promise<void>;
  shouldLogout: boolean;
}

export const useApprovalStatus = (user: User | null): UseApprovalStatusReturn => {
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldLogout, setShouldLogout] = useState(false);

  const fetchApprovalStatus = useCallback(async () => {
    if (!user) {
      setApprovalStatus(null);
      setIsLoading(false);
      setError(null);
      setShouldLogout(false);
      return;
    }

    // Don't make API calls for unverified users
    if (!user.emailVerified) {
      logger.debug("Skipping approval status check for unverified user");
      setApprovalStatus(null);
      setIsLoading(false);
      setError(null);
      setShouldLogout(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setShouldLogout(false);

    try {
      const status = await approvalService.getApprovalStatus();
      setApprovalStatus(status);
      logger.info("Approval status updated:", status);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to get approval status";
      setError(errorMessage);
      logger.error("Error fetching approval status:", err);

      // Check if this is a critical API failure that requires logout
      if (err instanceof Error && approvalService.isCriticalError(err)) {
        logger.warn("Critical API failure detected, triggering logout");
        setShouldLogout(true);

        // Automatically logout the user
        try {
          await signOutUser();
          logger.info("User automatically logged out due to API failure");
        } catch (logoutError) {
          logger.error("Error during automatic logout:", logoutError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchApprovalStatus();
  }, [fetchApprovalStatus]);

  const isApproved = approvalStatus?.approvalStatus === "approved";
  const isPending = approvalStatus?.approvalStatus === "pending";
  const isRejected = approvalStatus?.approvalStatus === "rejected";

  return {
    approvalStatus,
    isLoading,
    error,
    isApproved,
    isPending,
    isRejected,
    refetch: fetchApprovalStatus,
    shouldLogout,
  };
};
